import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, FolderGit2, Link2, Lock, Shapes } from "lucide-react";
import { Segmented } from "@/components/grooming/grooming-chips";
import { FogbugzLinksTab } from "@/components/grooming/project/fogbugz-links-tab";
import { MeetingScheduleTab } from "@/components/grooming/project/meeting-schedule-tab";
import { ProductAreasTab } from "@/components/grooming/project/product-areas-tab";
import { useGroomingConfig } from "@/hooks/queries/use-grooming";
import { canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { usePageTitle } from "@/hooks/use-page-title";
import { getApiErrorCode } from "@/lib/api/work-items";
import { DEFAULT_PROJECT_TAB, isProjectTab, projectPath, type ProjectTab } from "@/lib/grooming/paths";
import { cn } from "@/lib/utils";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { RepositoriesTab } from "./repos";

/** The Project page's subtitle (frames F1–F3). */
export function projectSubtitle(project: string): string {
  return `How ${project} is set up for grooming. You own the schedule and the product areas.`;
}

/**
 * Project (`/grooming/:project/project/:tab`; Project Managers and Admins,
 * `map-project`): the PM's project setup in one place (PKG-PM-F, feature
 * PI-SPEC-SW-1510; UX-PM-15 and 01-roles.md section 9.6; Claude Design
 * "PM Packages B E F Mockups" frames F1–F3). It replaced the Repos page:
 * the rail item, the command palette and the Queue's product-area link open
 * it, and `/grooming/:project/repos` redirects here.
 *
 * Three tabs in a segmented control beside the header:
 * - Meeting schedule (CalendarClock): when the grooming meeting repeats.
 * - Product areas (Shapes): the plain names every brief uses.
 * - Repositories: the old Code tab, unchanged inside (epic PI-SPEC-SW-E288:
 *   Admins edit, `manage-repos`; a Project Manager sees it read-only). Its
 *   icon is a lock for a viewer who may not edit the repositories, else
 *   FolderGit2.
 * - FogBugz (Link2; epic PI-SPEC-SW-E308, feature PI-SPEC-SW-1610): which
 *   FogBugz projects feed this ADO project, with per-area overrides. It does
 *   not need the grooming config, so it shows even before grooming is set up.
 *
 * An unknown tab opens the Meeting schedule. "Setup" stays the name of the
 * personal install checklist (`/toolbox/setup`).
 */
export function GroomingProjectPage() {
  const { project = "", tab: tabParam } = useParams();
  const navigate = useNavigate();
  usePageTitle(`Project · ${project}`);
  const tab: ProjectTab = isProjectTab(tabParam) ? tabParam : DEFAULT_PROJECT_TAB;
  const configQuery = useGroomingConfig(project);
  const notSetUp = getApiErrorCode(configQuery.error) === "GROOMING_CONFIG_NOT_FOUND";
  // E288: Admins set the repositories up; everyone else with `map-project` sees them read-only.
  const { data: access } = useToolboxAccess();
  const canManage = canOn(access, project, "manage-repos");

  return (
    <div className="flex flex-col gap-3.5" data-testid="grooming-project">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className={TOOLBOX_PAGE_TITLE}>Project</h1>
          <span className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")} data-testid="project-lead">
            {projectSubtitle(project)}
          </span>
        </div>
        <Segmented<ProjectTab>
          label="Project section"
          value={tab}
          onChange={(value) => {
            void navigate(projectPath(project, value), { replace: true });
          }}
          options={[
            { value: "meeting-schedule", label: "Meeting schedule", icon: CalendarClock },
            { value: "product-areas", label: "Product areas", icon: Shapes },
            { value: "repositories", label: "Repositories", icon: canManage ? FolderGit2 : Lock },
            { value: "fogbugz", label: "FogBugz", icon: Link2 },
          ]}
        />
      </div>
      {tab === "fogbugz" ? (
        <FogbugzLinksTab project={project} />
      ) : configQuery.isLoading ? (
        <div className="flex min-h-[200px] items-center justify-center" role="status" aria-live="polite">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="sr-only">Loading…</span>
        </div>
      ) : configQuery.isError && !notSetUp ? (
        <p className="text-sm text-destructive" role="alert">
          The project's grooming setup could not be loaded. Try again in a moment.
        </p>
      ) : tab === "repositories" ? (
        <RepositoriesTab project={project} config={configQuery.data} notSetUp={notSetUp} canManage={canManage} />
      ) : tab === "product-areas" ? (
        <ProductAreasTab project={project} config={configQuery.data} />
      ) : (
        <MeetingScheduleTab project={project} config={configQuery.data} />
      )}
    </div>
  );
}
